import type { ReactNode } from "react";
import { Link } from "react-router";
import { ArrowRight } from "lucide-react";
import { Container, Section, Heading, LinkButton } from "./ui";
import { SITE_URL } from "../lib/env";

export interface Faq {
  q: string;
  a: string;
}

/** Build FAQPage JSON-LD from the SAME items the page renders, so the
 *  structured data can never drift from the visible content (a Google
 *  rich-results requirement). Pass the result to <Seo jsonLd={...} />. */
export function faqPageJsonLd(items: Faq[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/** Visible FAQ — native <details> accordion (accessible, no JS). */
export function FaqSection({ items, heading = "Frequently asked questions" }: { items: Faq[]; heading?: string }) {
  return (
    <Section tone="soft">
      <Container>
        <Heading level={2}>{heading}</Heading>
        <div className="mt-8 max-w-3xl divide-y divide-border rounded-2xl border border-border bg-surface">
          {items.map((f) => (
            <details key={f.q} className="group px-6 py-5 [&_summary]:cursor-pointer">
              <summary className="flex items-center justify-between gap-4 text-base font-semibold text-ink-900 marker:content-['']">
                {f.q}
                <span className="text-forest-600 transition-transform group-open:rotate-45" aria-hidden>
                  +
                </span>
              </summary>
              <p className="mt-3 text-[0.9375rem] leading-relaxed text-ink-600">{f.a}</p>
            </details>
          ))}
        </div>
      </Container>
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Structured data shared across marketing pages.                      */
/* One definition each, referenced by @id, so the Organization and     */
/* SoftwareApplication nodes are identical on every page that carries  */
/* them. No ratings, reviews, or prices are asserted here.             */
/* ------------------------------------------------------------------ */

export const ORG_ID = `${SITE_URL}/#organization`;
export const SOFTWARE_ID = `${SITE_URL}/#software`;

/** Official profiles for Organization.sameAs. Only add URLs the company
 *  actually controls; an empty list is valid and better than a guessed one. */
export const ORG_SAME_AS: string[] = [];

export const ORGANIZATION_NODE = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: "WeConsult Rx",
  alternateName: "WeConsultRX",
  url: SITE_URL,
  logo: `${SITE_URL}/logo-icon.svg`,
  ...(ORG_SAME_AS.length ? { sameAs: ORG_SAME_AS } : {}),
  contactPoint: [
    { "@type": "ContactPoint", contactType: "sales", email: "hello@weconsultrx.com", areaServed: "US" },
  ],
  description:
    "Consultant pharmacy operations platform: clinical review engine, compliance automation, e-signature, and invoicing for SNF, ASC, and dialysis.",
};

export const SOFTWARE_NODE = {
  "@type": "SoftwareApplication",
  "@id": SOFTWARE_ID,
  name: "WeConsult Rx",
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "Consultant pharmacist software",
  operatingSystem: "Web",
  url: SITE_URL,
  publisher: { "@id": ORG_ID },
  offers: { "@type": "Offer", category: "SaaS", url: `${SITE_URL}/pricing` },
  audience: { "@type": "Audience", audienceType: "Consultant pharmacists" },
  featureList: [
    "Monthly medication regimen review (MRR) workflow",
    "Rule packs for Beers, STOPP/START, anticholinergic burden, and gradual dose reduction",
    "AI-drafted recommendation rationale with pharmacist sign-off",
    "Prescriber and DON e-signature",
    "Facility invoicing",
    "CMS F-tag aligned documentation and audit trail",
    "SNF, ASC, and dialysis compliance",
  ],
  description:
    "Consultant pharmacist software that runs the monthly medication regimen review end to end: chart ingest, rules and AI flags, prescriber and DON e-sign, and invoicing for SNF, ASC, and dialysis.",
};

/** BreadcrumbList from [name, path] pairs (path relative to SITE_URL). */
export function breadcrumbNode(trail: Array<[string, string]>) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: trail.map(([name, path], i) => ({
      "@type": "ListItem",
      position: i + 1,
      name,
      item: `${SITE_URL}${path}`,
    })),
  };
}

/** Wrap nodes in one @graph document. FAQPage nodes from faqPageJsonLd() lose
 *  their own @context here, which the graph-level @context covers. */
export function jsonLdGraph(...nodes: Array<Record<string, unknown>>) {
  return {
    "@context": "https://schema.org",
    "@graph": nodes.map(({ ["@context"]: _ctx, ...rest }) => rest),
  };
}

/** Contextual internal links (SEO: spreads link equity to the money pages). */
export function RelatedLinks({
  heading = "Related",
  items,
}: {
  heading?: string;
  items: { to: string; label: string; desc?: string }[];
}) {
  return (
    <Section>
      <Container>
        <Heading level={2}>{heading}</Heading>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              className="group rounded-2xl border border-border bg-surface p-5 transition-colors hover:border-forest-700"
            >
              <span className="flex items-center justify-between gap-2 font-semibold text-ink-900 group-hover:text-forest-700">
                {it.label}
                <ArrowRight className="h-4 w-4 flex-none text-forest-700" aria-hidden />
              </span>
              {it.desc ? <p className="mt-1.5 text-sm leading-relaxed text-ink-600">{it.desc}</p> : null}
            </Link>
          ))}
        </div>
      </Container>
    </Section>
  );
}

/** Dark conversion band used at the foot of the marketing landing pages. */
export function CtaBand({
  title,
  body,
  primary = { to: "/demo", label: "Book a demo" },
  secondary,
}: {
  title: ReactNode;
  body: ReactNode;
  primary?: { to: string; label: string };
  secondary?: { to: string; label: string };
}) {
  return (
    <Section>
      <Container>
        <div className="rounded-3xl bg-forest-gradient px-8 py-14 text-center text-white sm:px-14">
          <Heading level={2} className="text-white">
            {title}
          </Heading>
          <p className="mx-auto mt-4 max-w-2xl text-white/85">{body}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <LinkButton to={primary.to} variant="secondary">
              {primary.label}
            </LinkButton>
            {secondary ? (
              <LinkButton to={secondary.to} variant="ghost" className="text-white hover:bg-white/10">
                {secondary.label}
              </LinkButton>
            ) : null}
          </div>
        </div>
      </Container>
    </Section>
  );
}
